import { useEffect, useRef, useState } from "react";
import { useAuth, useAuthActions } from "@frontegg/react";
import { Link, useLocation } from "react-router-dom";
import { LogOut, Shield, Link2, User } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useIsAdmin } from "../../../hooks/useIsAdmin";

interface AdminEntry {
  label: string;
  to: string;
  icon: LucideIcon;
}

const ADMIN_ENTRIES: AdminEntry[] = [
  { label: "Share Links", to: "/admin/share-links", icon: Link2 },
];

export default function HeaderUserActions() {
  const auth = useAuth();
  const actions = useAuthActions();
  const { isAdmin } = useIsAdmin();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!auth?.user) return null;

  const displayName = auth.user.name || auth.user.email || "Account";

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="rounded-full focus:outline-none focus:ring-2 focus:ring-[#0193F8]/60"
      >
        {auth.user.profilePictureUrl ? (
          <img
            src={auth.user.profilePictureUrl}
            alt="Profile"
            className="w-[40px] h-[40px] rounded-full block"
          />
        ) : (
          <div className="w-[40px] h-[40px] rounded-full bg-primary-200/15 flex items-center justify-center text-[#DDEBFF]">
            <User size={18} />
          </div>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-3 w-72 bg-[#05050F] border border-primary-400/30 rounded-xl shadow-2xl overflow-hidden z-50"
        >
          <div className="px-4 py-3 border-b border-primary-400/20">
            <div className="text-[#DDEBFF] text-sm font-semibold truncate">
              {displayName}
            </div>
            {auth.user.email && (
              <div className="text-[#6B96D6] text-xs truncate mt-0.5">
                {auth.user.email}
              </div>
            )}
          </div>

          {isAdmin && (
            <div className="py-2 border-b border-primary-400/20">
              <div className="px-4 pb-1 flex items-center gap-1.5 text-[#6B96D6] text-[10px] uppercase tracking-wider">
                <Shield size={11} />
                Admin
              </div>
              {ADMIN_ENTRIES.map((entry) => {
                const Icon = entry.icon;
                return (
                  <Link
                    key={entry.to}
                    to={entry.to}
                    role="menuitem"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2.5 px-4 py-2 text-sm text-[#DDEBFF] hover:bg-primary-200/10 transition-colors"
                  >
                    <Icon size={14} className="text-[#6B96D6]" />
                    {entry.label}
                  </Link>
                );
              })}
            </div>
          )}

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              actions.logout();
            }}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#DDEBFF] hover:bg-primary-200/10 transition-colors"
          >
            <LogOut size={14} className="text-[#6B96D6]" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
